import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchJson } from "../lib/api";
import { useTheme } from "../stores/useTheme";
import { TerminalLine } from "../components/terminal/TerminalLine";
import { supabase } from "../lib/supabase";
import { useUserStats } from "../hooks/queries";
import { UserProfileCard } from "../components/terminal/UserProfileCard";

interface ProviderRow {
  id: string;
  name: string;
  baseUrl: string;
  modelId: string;
  providerType: "anthropic" | "openai" | "openrouter" | "groq" | "ollama";
  isActive: boolean;
  hasApiKey: boolean;
  createdAt: string;
}

const PROVIDER_TYPES: ProviderRow["providerType"][] = ["anthropic", "openai", "openrouter", "groq", "ollama"];

export function SettingsPage() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
      <TerminalLine prompt accent>settings</TerminalLine>

      <CollapsibleSection title="profile setup" defaultOpen={false}>
        <ProfileSetupSection />
      </CollapsibleSection>

      <CollapsibleSection title="appearance">
        <AppearanceSection />
      </CollapsibleSection>

      <CollapsibleSection title="ai providers">
        <ProvidersSection />
      </CollapsibleSection>

      <CollapsibleSection title="integrations">
        <IntegrationsSection />
      </CollapsibleSection>

      <CollapsibleSection title="notifications">
        <NotificationsSection />
      </CollapsibleSection>

      <CollapsibleSection title="data management">
        <DataSection />
      </CollapsibleSection>

      <CollapsibleSection title="session">
        <SessionSection />
      </CollapsibleSection>
    </div>
  );
}

function CollapsibleSection({ title, children, defaultOpen = false }: { title: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="shukan-collapsible">
      <div 
        className={`shukan-collapsible-header ${open ? "open" : ""}`}
        onClick={() => setOpen(!open)}
      >
        <span>$ {title}</span>
        <span>{open ? "[-]" : "[+]"}</span>
      </div>
      {open && <div className="shukan-collapsible-content">{children}</div>}
    </div>
  );
}

function ProfileSetupSection() {
  const queryClient = useQueryClient();
  const userStats = useUserStats();
  
  const [username, setUsername] = useState("");
  const [customQuote, setCustomQuote] = useState("");
  const [avatarAscii, setAvatarAscii] = useState("");
  const [saveStatus, setSaveStatus] = useState<string | null>(null);
  const [customTitle, setCustomTitle] = useState("");

  useEffect(() => {
    if (userStats.data) {
      setUsername(userStats.data.username ?? "shukan_user");
      setCustomQuote(userStats.data.customQuote ?? "A clean space is a productive space.");
      setAvatarAscii(userStats.data.avatarAscii ?? "");
      setCustomTitle(userStats.data.customTitle ?? "Habit Seeker");
    }
  }, [userStats.data]);

  const saveProfile = useMutation({
    mutationFn: async (vars: { username: string; customQuote: string; avatarAscii: string; customTitle: string }) => {
      return fetchJson("/api/user/stats", {
        method: "PATCH",
        body: JSON.stringify(vars),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-stats"] });
      setSaveStatus("✓ profile saved successfully");
      setTimeout(() => setSaveStatus(null), 3000);
    },
    onError: (err) => {
      setSaveStatus(`✗ failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  });

  const presets = [
    { name: "Greenhorn", avatar: "  ( ^_^)\n  /|_|\\\n  /   \\" },
    { name: "Novice Planer", avatar: "  [  -_- ]\n  /|▓▓▓|\\\n  /   \\" },
    { name: "Aurora Seeker", avatar: "  \\('o')/\n  /|▓▓▓|\\\n  /   \\" },
    { name: "Mint Alchemist", avatar: "  (•‿•)\n  /|███|\\\n  /   \\" },
    { name: "Volcanic Druid", avatar: " (🔥_🔥)\n/|███|\\\n /   \\" },
    { name: "Cyber Warrior", avatar: "  (★_★)\n/|⚡▓⚡|\\\n /     \\" },
    { name: "Forest Ranger", avatar: "  (☘_☘)\n  /|▓▓▓|\\\n  /   \\" },
    { name: "Ocean Vanguard", avatar: "  (⚙_⚙)\n  /|☸▓☸|\\\n  /   \\" },
    { name: "Retro Runner", avatar: " (⚡_⚡)\n/|█▓█|\\\n /   \\" },
    { name: "Solar Knight", avatar: " (☀️_☀️)\n/|⚔️█⚔️|\\\n /     \\" },
    { name: "Cosmic Overlord", avatar: "  (☯_☯)\n/|⚛️█⚛️|\\\n /     \\" },
    { name: "Amethyst Mage", avatar: " (✨_✨)\n/|🔮█🔮|\\\n /     \\" },
    { name: "Dimension Shifter", avatar: " (👁️_👁️)\n/|🌀█🌀|\\\n /     \\" },
    { name: "Zen Master AGI", avatar: " (🌟_🌟)\n/|☯️█☯️|\\\n /     \\" }
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      {userStats.data && (
        <UserProfileCard stats={{ ...userStats.data, username, customQuote, avatarAscii, customTitle }} />
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
        <label style={{ fontSize: "0.75rem", color: "var(--fg-dim)" }}>$ username</label>
        <input 
          className="shukan-input" 
          value={username} 
          onChange={(e) => setUsername(e.target.value)} 
          placeholder="username" 
        />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
        <label style={{ fontSize: "0.75rem", color: "var(--fg-dim)" }}>$ select active title</label>
        <select
          className="shukan-input"
          value={customTitle}
          onChange={(e) => setCustomTitle(e.target.value)}
          style={{ fontSize: "0.75rem", fontFamily: "monospace", padding: "0.3rem" }}
        >
          {[
            { level: 1, name: "Greenhorn" },
            { level: 5, name: "Sunset Seeker" },
            { level: 8, name: "Aurora Seeker" },
            { level: 10, name: "Mint Alchemist" },
            { level: 12, name: "Volcanic Druid" },
            { level: 15, name: "Cyber Warrior" },
            { level: 18, name: "Forest Ranger" },
            { level: 20, name: "Ocean Vanguard" },
            { level: 22, name: "Retro Runner" },
            { level: 25, name: "Solar Knight" },
            { level: 30, name: "Cosmic Overlord" },
            { level: 35, name: "Amethyst Mage" },
            { level: 40, name: "Dimension Shifter" },
            { level: 50, name: "Zen Master AGI" },
          ].map((t) => {
            const userLevel = userStats.data?.level ?? 0;
            const isUnlocked = userLevel >= t.level;
            return (
              <option key={t.name} value={t.name} disabled={!isUnlocked}>
                {t.name} {isUnlocked ? "" : `(Locked - Lvl ${t.level})`}
              </option>
            );
          })}
        </select>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
        <label style={{ fontSize: "0.75rem", color: "var(--fg-dim)" }}>$ custom motto / daily quote</label>
        <input 
          className="shukan-input" 
          value={customQuote} 
          onChange={(e) => setCustomQuote(e.target.value)} 
          placeholder="Motto..." 
        />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
        <label style={{ fontSize: "0.75rem", color: "var(--fg-dim)" }}>$ select ascii avatar preset</label>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))", gap: "0.5rem", marginTop: "0.25rem" }}>
          {presets.map((p, idx) => (
            <button
              key={idx}
              type="button"
              className="shukan-btn"
              style={{
                height: "6.5rem",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "0.4rem",
                borderColor: avatarAscii.trim() === p.avatar.trim() ? "var(--accent)" : "var(--border)",
              }}
              onClick={() => setAvatarAscii(p.avatar)}
            >
              <span style={{ fontSize: "0.6rem", color: "var(--fg-dim)" }}>{p.name}</span>
              <pre style={{ margin: 0, fontSize: "0.6rem", fontFamily: "'JetBrains Mono', monospace", color: avatarAscii.trim() === p.avatar.trim() ? "var(--accent)" : "var(--fg)" }}>{p.avatar}</pre>
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
        <label style={{ fontSize: "0.75rem", color: "var(--fg-dim)" }}>$ custom ascii avatar (paste or edit)</label>
        <textarea 
          className="shukan-input"
          style={{ minHeight: "6rem", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.75rem" }}
          value={avatarAscii}
          onChange={(e) => setAvatarAscii(e.target.value)}
          placeholder="Paste custom ASCII art here..."
        />
      </div>

      <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginTop: "0.25rem" }}>
        <button 
          type="button" 
          className="shukan-btn shukan-btn-primary" 
          onClick={() => saveProfile.mutate({ username, customQuote, avatarAscii, customTitle })}
          disabled={saveProfile.isPending}
        >
          {saveProfile.isPending ? "$ saving..." : "$ save profile"}
        </button>
        {saveStatus && <span style={{ fontSize: "0.75rem", color: "var(--fg-dim)" }}>{saveStatus}</span>}
      </div>
    </div>
  );
}

function AppearanceSection() {
  const accent = useTheme((s) => s.accent);
  const setAccent = useTheme((s) => s.setAccent);
  const fg = useTheme((s) => s.fg);
  const fgDim = useTheme((s) => s.fgDim);
  const setFg = useTheme((s) => s.setFg);
  const setFgDim = useTheme((s) => s.setFgDim);
  const setThemePair = useTheme((s) => s.setThemePair);
  const userStats = useUserStats();
  const [showGradients, setShowGradients] = useState(false);

  const saveMutation = useMutation({
    mutationFn: async (next: string) =>
      fetchJson("/api/user/stats", { method: "PATCH", body: JSON.stringify({ accent: next }) }),
  });

  function applyAndSave(hex: string) {
    setAccent(hex);
    saveMutation.mutate(hex);
  }

  const presets = [
    "#f59e0b", "#22d3ee", "#34d399", "#f472b6", "#a78bfa", "#f87171",
    "#6366f1", "#14b8a6", "#f97316", "#3b82f6", "#84cc16", "#06b6d4",
    "#64748b", "#f43f5e", "#ef4444", "#eab308",
  ];

  const gradientPresets = [
    { id: "gradient-sunset", name: "Sunset Glow", level: 5, css: "linear-gradient(135deg, #f59e0b, #ef4444)" },
    { id: "gradient-aurora", name: "Aurora Borealis", level: 8, css: "linear-gradient(135deg, #0284c7, #10b981)" },
    { id: "gradient-mint", name: "Neon Mint", level: 10, css: "linear-gradient(135deg, #10b981, #06b6d4)" },
    { id: "gradient-volcano", name: "Volcanic Ash", level: 12, css: "linear-gradient(135deg, #ef4444, #7f1d1d)" },
    { id: "gradient-cyber", name: "Cyber Purple", level: 15, css: "linear-gradient(135deg, #8b5cf6, #ec4899)" },
    { id: "gradient-forest", name: "Forest Dew", level: 18, css: "linear-gradient(135deg, #059669, #84cc16)" },
    { id: "gradient-ocean", name: "Ocean Breeze", level: 20, css: "linear-gradient(135deg, #3b82f6, #22d3ee)" },
    { id: "gradient-vaporwave", name: "Retro Vapor", level: 22, css: "linear-gradient(135deg, #ff007f, #00f2fe)" },
    { id: "gradient-solar", name: "Solar Flare", level: 25, css: "linear-gradient(135deg, #f97316, #eab308)" },
    { id: "gradient-cosmic", name: "Cosmic Fire", level: 30, css: "linear-gradient(135deg, #f43f5e, #fb7185)" },
    { id: "gradient-amethyst", name: "Royal Amethyst", level: 35, css: "linear-gradient(135deg, #6366f1, #d946ef)" },
    { id: "gradient-midnight", name: "Midnight Cyber", level: 40, css: "linear-gradient(135deg, #be123c, #1e1b4b)" },
    { id: "gradient-zen", name: "Zen Master", level: 50, css: "linear-gradient(135deg, #4b5563, #1f2937)" },
  ];

  const fontPairTemplates = [
    { name: "Default Gray", fg: "#e5e5e5", fgDim: "#8a8a8a" },
    { name: "Pure Terminal", fg: "#39ff14", fgDim: "#047857" },
    { name: "Cyber Sunset", fg: "#ff007f", fgDim: "#7c3aed" },
    { name: "Retro Amber", fg: "#fef08a", fgDim: "#ca8a04" },
    { name: "Arctic Ice", fg: "#ecfeff", fgDim: "#0891b2" },
    { name: "Classic Monochrome", fg: "#ffffff", fgDim: "#6b7280" },
  ];

  const inputColorValue = accent.startsWith("gradient-") ? "#f59e0b" : accent;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <div>
        <div style={{ fontSize: "0.75rem", color: "var(--fg-dim)", marginBottom: "0.5rem" }}>
          &gt; Accent Color Customization
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <input
            type="color"
            value={inputColorValue}
            onChange={(e) => applyAndSave(e.target.value)}
            style={{ width: "2.5rem", height: "2.5rem", background: "transparent", border: "1px solid var(--border)", cursor: "pointer" }}
          />
          <input
            className="shukan-input"
            style={{ maxWidth: "10rem" }}
            value={accent}
            onChange={(e) => setAccent(e.target.value)}
            onBlur={() => saveMutation.mutate(accent)}
          />
          <span style={{ color: "var(--fg-dim)", fontSize: "0.75rem" }}>{userStats.data ? `level ${userStats.data.level} · ${userStats.data.totalXp}xp` : ""}</span>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem", flexWrap: "wrap" }}>
          {presets.map((c) => (
            <button key={c} type="button" onClick={() => applyAndSave(c)} aria-label={`accent ${c}`}
              style={{
                width: "1.5rem", height: "1.5rem", borderRadius: "2px",
                background: c, border: c === accent ? "2px solid var(--fg)" : "1px solid var(--border)",
              }} />
          ))}
        </div>
      </div>

      <button 
        type="button" 
        className="shukan-btn" 
        style={{ alignSelf: "flex-start" }}
        onClick={() => setShowGradients(!showGradients)}
      >
        {showGradients ? "$ hide premium gradient themes" : "$ show premium gradient themes"}
      </button>

      {showGradients && (
        <div>
          <div style={{ fontSize: "0.75rem", color: "var(--fg-dim)", marginBottom: "0.5rem" }}>
            &gt; premium gradient themes (unlocked by level)
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: "0.75rem" }}>
            {gradientPresets.map((g) => {
              const currentLevel = userStats.data?.level ?? 0;
              const isUnlocked = currentLevel >= g.level;
              const isSelected = accent === g.id;
              
              return (
                <button
                  key={g.id}
                  type="button"
                  disabled={!isUnlocked}
                  onClick={() => applyAndSave(g.id)}
                  style={{
                    height: "2.75rem",
                    borderRadius: "4px",
                    background: isUnlocked ? g.css : "var(--bg-elevated)",
                    border: isSelected ? "2px solid var(--fg)" : isUnlocked ? "1px solid var(--border)" : "1px dashed var(--border)",
                    color: isUnlocked ? "#ffffff" : "var(--fg-dim)",
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: isUnlocked ? "pointer" : "not-allowed",
                    position: "relative",
                    boxShadow: isSelected ? "0 0 10px var(--accent-glow)" : "none",
                    textShadow: isUnlocked ? "0 1px 2px rgba(0, 0, 0, 0.8)" : "none",
                  }}
                >
                  <span>{g.name}</span>
                  <span style={{ fontSize: "0.6rem", opacity: 0.8, color: isUnlocked ? "#ffffff" : "var(--danger)" }}>
                    {isUnlocked ? `unlocked` : `🔒 lv. ${g.level}`}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Font Colors Section */}
      <div style={{ borderTop: "1px dashed var(--border)", paddingTop: "1rem", marginTop: "0.5rem" }}>
        <div style={{ fontSize: "0.75rem", color: "var(--fg-dim)", fontWeight: "bold", marginBottom: "0.5rem" }}>
          &gt; Font Color Customization
        </div>
        
        <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", marginBottom: "0.75rem" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
            <label style={{ fontSize: "0.7rem", color: "var(--fg-dim)" }}>$ primary font color</label>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <input
                type="color"
                value={fg}
                onChange={(e) => setFg(e.target.value)}
                style={{ width: "2rem", height: "2.1rem", background: "transparent", border: "1px solid var(--border)", cursor: "pointer" }}
              />
              <input
                className="shukan-input"
                style={{ maxWidth: "7rem", fontSize: "0.75rem" }}
                value={fg}
                onChange={(e) => setFg(e.target.value)}
              />
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
            <label style={{ fontSize: "0.7rem", color: "var(--fg-dim)" }}>$ secondary font color</label>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <input
                type="color"
                value={fgDim}
                onChange={(e) => setFgDim(e.target.value)}
                style={{ width: "2rem", height: "2.1rem", background: "transparent", border: "1px solid var(--border)", cursor: "pointer" }}
              />
              <input
                className="shukan-input"
                style={{ maxWidth: "7rem", fontSize: "0.75rem" }}
                value={fgDim}
                onChange={(e) => setFgDim(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Font Pairing Templates */}
        <div style={{ fontSize: "0.7rem", color: "var(--fg-dim)", marginBottom: "0.5rem" }}>
          &gt; Predefined Font Templates
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(135px, 1fr))", gap: "0.5rem" }}>
          {fontPairTemplates.map((t, idx) => {
            const isSelected = fg.toLowerCase() === t.fg.toLowerCase() && fgDim.toLowerCase() === t.fgDim.toLowerCase();
            return (
              <button
                key={idx}
                type="button"
                className="shukan-btn"
                onClick={() => setThemePair(t.fg, t.fgDim)}
                style={{
                  padding: "0.4rem",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  borderColor: isSelected ? "var(--accent)" : "var(--border)",
                  background: "var(--bg-elevated)",
                }}
              >
                <span style={{ fontSize: "0.75rem", fontWeight: "bold" }}>{t.name}</span>
                <div style={{ display: "flex", gap: "0.3rem", marginTop: "0.25rem" }}>
                  <span style={{ width: "0.6rem", height: "0.6rem", background: t.fg, border: "1px solid var(--border)", borderRadius: "1px" }} title="Primary font color" />
                  <span style={{ width: "0.6rem", height: "0.6rem", background: t.fgDim, border: "1px solid var(--border)", borderRadius: "1px" }} title="Secondary font color" />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ProvidersSection() {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["ai-providers"],
    queryFn: () => fetchJson<ProviderRow[]>("/api/ai/providers"),
  });
  const [name, setName] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [modelId, setModelId] = useState("");
  const [providerType, setProviderType] = useState<ProviderRow["providerType"]>("openai");
  const [testResult, setTestResult] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () => fetchJson<ProviderRow>("/api/ai/providers", {
      method: "POST",
      body: JSON.stringify({ name, baseUrl, apiKey, modelId, providerType, isActive: false }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ai-providers"] });
      setName(""); setBaseUrl(""); setApiKey(""); setModelId("");
      setTestResult("✓ provider saved successfully");
    },
    onError: (err) => {
      setTestResult(`✗ save failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  });

  const activate = useMutation({
    mutationFn: async (id: string) => fetchJson<unknown>(`/api/ai/providers/${id}/activate`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ai-providers"] });
      qc.invalidateQueries({ queryKey: ["user-stats"] });
    }
  });

  const remove = useMutation({
    mutationFn: async (id: string) => fetchJson<unknown>(`/api/ai/providers/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ai-providers"] }),
  });

  async function test() {
    setTestResult("testing connection...");
    try {
      const res = await fetchJson<{ ok: boolean; model?: string; error?: string }>("/api/ai/test-provider", {
        method: "POST",
        body: JSON.stringify({ baseUrl, apiKey, modelId, providerType }),
      });
      if (res.ok) {
        setTestResult(`✓ connection ok: ${res.model ?? modelId}`);
      } else {
        setTestResult(`✗ failed: ${res.error ?? "unknown error"}`);
      }
    } catch (e) {
      setTestResult(`✗ error: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      <div style={{ fontSize: "0.75rem", color: "var(--fg-dim)" }}>
        Add an LLM provider to power Shukan's AI check-ins, audits, and daily schedule generation.
      </div>

      {list.data && list.data.length > 0 && (
        <div style={{ border: "1px dashed var(--border)", padding: "0.5rem" }}>
          <div style={{ fontSize: "0.7rem", color: "var(--fg-dim)", marginBottom: "0.25rem" }}>&gt; configured providers</div>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.25rem" }}>
            {list.data.map((p) => (
              <li key={p.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "0.75rem" }}>
                <span>
                  {p.isActive ? <strong style={{ color: "var(--accent)" }}>● {p.name}</strong> : `○ ${p.name}`}{" "}
                  <span style={{ color: "var(--fg-dim)" }}>({p.providerType} · {p.modelId})</span>
                </span>
                <div style={{ display: "flex", gap: "0.25rem" }}>
                  {!p.isActive && (
                    <button type="button" className="shukan-btn" style={{ fontSize: "0.65rem", padding: "0.1rem 0.3rem" }} onClick={() => activate.mutate(p.id)}>
                      activate
                    </button>
                  )}
                  <button type="button" className="shukan-btn" style={{ fontSize: "0.65rem", padding: "0.1rem 0.3rem", color: "var(--danger)" }} onClick={() => remove.mutate(p.id)}>
                    delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); create.mutate(); }} style={{ display: "flex", flexDirection: "column", gap: "0.5rem", borderTop: "1px solid var(--border)", paddingTop: "0.75rem" }}>
        <div style={{ fontSize: "0.75rem", fontWeight: "bold" }}>&gt; add new provider</div>
        
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "0.2rem" }}>
            <label style={{ fontSize: "0.65rem", color: "var(--fg-dim)" }}>name</label>
            <input className="shukan-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. OpenRouter Anthropic" required />
          </div>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "0.2rem" }}>
            <label style={{ fontSize: "0.65rem", color: "var(--fg-dim)" }}>provider type</label>
            <select className="shukan-input" value={providerType} onChange={(e) => setProviderType(e.target.value as any)}>
              {PROVIDER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}>
          <label style={{ fontSize: "0.65rem", color: "var(--fg-dim)" }}>base url</label>
          <input className="shukan-input" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="e.g. https://openrouter.ai/api/v1" required />
        </div>

        <div style={{ display: "flex", gap: "0.5rem" }}>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "0.2rem" }}>
            <label style={{ fontSize: "0.65rem", color: "var(--fg-dim)" }}>model id</label>
            <input className="shukan-input" value={modelId} onChange={(e) => setModelId(e.target.value)} placeholder="e.g. anthropic/claude-3.5-sonnet" required />
          </div>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "0.2rem" }}>
            <label style={{ fontSize: "0.65rem", color: "var(--fg-dim)" }}>api key</label>
            <input type="password" className="shukan-input" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="••••••••••••" required />
          </div>
        </div>

        <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.25rem", alignItems: "center" }}>
          <button type="submit" className="shukan-btn shukan-btn-primary" disabled={create.isPending}>
            $ save provider
          </button>
          <button type="button" className="shukan-btn" onClick={test} disabled={!baseUrl || !apiKey || !modelId}>
            test connection
          </button>
          {testResult && <span style={{ fontSize: "0.75rem", color: "var(--fg-dim)" }}>{testResult}</span>}
        </div>
      </form>
    </div>
  );
}

function IntegrationsSection() {
  const qc = useQueryClient();
  const status = useQuery({
    queryKey: ["gcal-status"],
    queryFn: () => fetchJson<{ connected: boolean; expiresAt: string | null }>("/api/calendar/status"),
  });

  const connect = useMutation({
    mutationFn: async () => fetchJson<{ authUrl: string }>("/api/calendar/connect"),
    onSuccess: (res) => {
      window.location.href = res.authUrl;
    }
  });

  const disconnect = useMutation({
    mutationFn: async () => fetchJson<unknown>("/api/calendar", { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["gcal-status"] }),
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
      <div style={{ fontSize: "0.75rem", color: "var(--fg-dim)" }}>
        Sync read-only events from your Google Calendar. Connect to list calendar schedule overlays in check-ins and the custom calendar view.
      </div>
      {status.data && (
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          {status.data.connected ? (
            <>
              <span style={{ fontSize: "0.75rem", color: "var(--success)" }}>✓ connected to google calendar</span>
              <button type="button" className="shukan-btn" style={{ color: "var(--danger)" }} onClick={() => disconnect.mutate()}>
                disconnect
              </button>
            </>
          ) : (
            <button type="button" className="shukan-btn shukan-btn-primary" onClick={() => connect.mutate()} disabled={connect.isPending}>
              {connect.isPending ? "..." : "$ connect google calendar"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function NotificationsSection() {
  const [subs, setSubs] = useState<Array<{ id: string; endpoint: string; reminder_enabled: boolean }>>([]);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [broadcastTitle, setBroadcastTitle] = useState("Shukan Announcement");
  const [broadcastBody, setBroadcastBody] = useState("Time to complete your goals!");

  async function refresh() {
    try {
      const list = await fetchJson<Array<{ id: string; endpoint: string; reminder_enabled: boolean }>>(
        "/api/notifications",
      );
      setSubs(list);
    } catch {
      // Non-fatal
    }
  }

  async function subscribeDevice() {
    setBusy(true);
    setStatus("requesting permission...");
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        throw new Error("Push notifications not supported on this device/browser");
      }

      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        throw new Error("Notification permission denied");
      }

      setStatus("fetching server configuration...");
      const { publicKey } = await fetchJson<{ publicKey: string | null }>("/api/notifications/vapid-key");
      if (!publicKey) {
        throw new Error("Server has not configured VAPID keys. Check VAPID_PUBLIC_KEY secret.");
      }

      setStatus("registering with push service...");
      const reg = await navigator.serviceWorker.ready;
      
      const existingSub = await reg.pushManager.getSubscription();
      if (existingSub) {
        await existingSub.unsubscribe();
      }

      const convertedKey = urlBase64ToUint8Array(publicKey);
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertedKey,
      });

      setStatus("saving subscription to server...");
      const subJson = sub.toJSON();
      if (!subJson.endpoint || !subJson.keys?.p256dh || !subJson.keys?.auth) {
        throw new Error("Invalid subscription object from browser");
      }

      await fetchJson("/api/notifications", {
        method: "POST",
        body: JSON.stringify({
          endpoint: subJson.endpoint,
          keys: {
            p256dh: subJson.keys.p256dh,
            auth: subJson.keys.auth,
          },
        }),
      });

      setStatus("✓ subscribed successfully");
      refresh();
    } catch (e) {
      setStatus(`✗ ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  async function removeSub(id: string) {
    try {
      await fetchJson(`/api/notifications/${id}`, { method: "DELETE" });
      refresh();
    } catch (e) {
      setStatus(`✗ failed to delete subscription`);
    }
  }

  async function sendTest() {
    setBusy(true);
    setStatus(null);
    try {
      const res = await fetchJson<{ ok: boolean; sent?: number; error?: string }>(
        "/api/notifications/test",
        { method: "POST" },
      );
      if (!res.ok) {
        setStatus(`✗ ${res.error ?? "failed"}`);
        return;
      }
      setStatus(`✓ sent — check your device (${res.sent ?? 1} delivered)`);
    } catch (e) {
      setStatus(`✗ ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  async function sendBroadcast() {
    setBusy(true);
    setStatus(null);
    try {
      const res = await fetchJson<{ ok: boolean; sent?: number; error?: string }>(
        "/api/notifications/broadcast",
        {
          method: "POST",
          body: JSON.stringify({ title: broadcastTitle, body: broadcastBody }),
        },
      );
      if (!res.ok) {
        setStatus(`✗ ${res.error ?? "failed"}`);
        return;
      }
      setStatus(`✓ Broadcasted — ${res.sent ?? 0} devices notified`);
    } catch (e) {
      setStatus(`✗ ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => { refresh(); }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
      <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
        <button type="button" className="shukan-btn shukan-btn-primary" disabled={busy} onClick={subscribeDevice}>
          $ subscribe this device
        </button>
        <button type="button" className="shukan-btn" disabled={busy || subs.length === 0} onClick={sendTest}>
          $ send test push
        </button>
        {status ? <span style={{ fontSize: "0.75rem", color: "var(--fg-dim)" }}>{status}</span> : null}
      </div>

      {subs.length > 0 && (
        <div style={{ marginTop: "0.5rem", border: "1px dashed var(--border)", padding: "0.5rem" }}>
          <div style={{ fontSize: "0.75rem", color: "var(--fg-dim)", marginBottom: "0.25rem" }}>
            &gt; active device list ({subs.length})
          </div>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.25rem" }}>
            {subs.map((s) => (
              <li key={s.id} style={{ display: "flex", justifySelf: "stretch", justifyContent: "space-between", alignItems: "center", fontSize: "0.75rem", color: "var(--fg-dim)" }}>
                <span>↳ {getDeviceLabel(s.endpoint)}</span>
                <button
                  type="button"
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "var(--danger)",
                    cursor: "pointer",
                    fontSize: "0.9rem",
                    padding: "0 0.5rem",
                  }}
                  onClick={() => removeSub(s.id)}
                  title="Remove device"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div style={{ marginTop: "1rem", borderTop: "1px dashed var(--border)", paddingTop: "0.75rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        <div style={{ fontSize: "0.75rem", color: "var(--fg-dim)" }}>&gt; broadcast notification to all devices</div>
        <input 
          className="shukan-input" 
          value={broadcastTitle} 
          onChange={(e) => setBroadcastTitle(e.target.value)} 
          placeholder="Notification Title" 
          style={{ fontSize: "0.75rem" }}
        />
        <textarea 
          className="shukan-input" 
          value={broadcastBody} 
          onChange={(e) => setBroadcastBody(e.target.value)} 
          placeholder="Notification Body text..." 
          style={{ fontSize: "0.75rem", minHeight: "3rem" }}
        />
        <button 
          type="button" 
          className="shukan-btn" 
          style={{ alignSelf: "flex-start", fontSize: "0.75rem" }}
          disabled={busy || !broadcastTitle.trim() || !broadcastBody.trim()} 
          onClick={sendBroadcast}
        >
          $ broadcast notification
        </button>
      </div>
    </div>
  );
}

function getDeviceLabel(endpoint: string): string {
  try {
    const host = new URL(endpoint).hostname;
    if (host.includes("googleapis.com")) return "Chrome / Android";
    if (host.includes("apple.com")) return "Safari / Apple Device";
    if (host.includes("mozilla.com")) return "Firefox Device";
    if (host.includes("windows.com")) return "Edge / Windows Device";
    return host;
  } catch {
    return "Unknown Device";
  }
}

function urlBase64ToUint8Array(base64String: string) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function DataSection() {
  const [busy, setBusy] = useState<"export" | "import" | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  async function doExport() {
    setBusy("export");
    try {
      const token = (await import("../lib/supabase")).getAccessToken();
      const blob = await fetch(`${import.meta.env.VITE_API_URL}/api/export`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }).then((r) => r.blob());
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `shukan-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setMsg("✓ exported");
    } catch (e) {
      setMsg(`✗ ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
      <button type="button" className="shukan-btn" disabled={busy !== null} onClick={doExport}>
        {busy === "export" ? "..." : "$ export json"}
      </button>
      <button type="button" className="shukan-btn" disabled title="import wired in Phase 3 follow-up">$ import json</button>
      {msg ? <span style={{ fontSize: "0.75rem", color: "var(--fg-dim)" }}>{msg}</span> : null}
    </div>
  );
}

function SessionSection() {
  return (
    <button type="button" className="shukan-btn" onClick={() => supabase.auth.signOut()}>$ sign out</button>
  );
}
